import {z} from "zod";
import type { AssertSchemaMatchesClass } from "../../../utils/typeUtils";
import { DeploymentCharacteristic, DeploymentProfile, DeploymentRequirementsSection, ExecutiveReadinessVerdict, KeyRisk, LeadershipSummary, OrganisationalImpact, RiskDriver, RiskImpactSummary, UseCaseOverview, RiskPlusTitlePage, RiskPlusReport, DeploymentRequirement, RiskProfilePillar, RiskProfileSummary } from "../RiskPlusReport";
import { RiskRanks, RiskRanksWithNA } from "../../risk/Risk";
import { AISystemImpact } from "../../risk/AISystemImpact";
import { AISystemImpactSchema } from "../../risk/schemas/AISystemImpact.schema";
import { MitigationRequirementSchema, RiskRegistryItemSchema } from "./RiskRegistry.schema";


const _RiskPlusTitlePageZod = z.object({
  title: z.string().default(""),
  subtitle: z.string().default(""),
  useCaseTitle: z.string().default(""),
  client: z.string().default(""),
  reportDate: z.coerce.date().default(new Date()),
  riskAssessmentStage: z.string().default("")
})

// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _RiskPlusTitlePageZod, RiskPlusTitlePage>);

export const RiskPlusTitlePageSchema = _RiskPlusTitlePageZod.transform((data) => data as RiskPlusTitlePage);



const _RiskImpactSummaryZod = z.object({
  impactArea: z.string().default(""),
  riskRating: z.enum(RiskRanks).default(RiskRanks[0]),
  description: z.string().default("")
})
// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _RiskImpactSummaryZod, RiskImpactSummary>);
export const RiskImpactSummarySchema = _RiskImpactSummaryZod.transform((data) => new RiskImpactSummary(data));



const _ExecutiveReadinessVerdictZod = z.object({
  overallVerdict: z.string().default(""),
  overallRiskRank: z.enum(RiskRanks).default(RiskRanks[0]),
  useCaseImpacts: z.array(RiskImpactSummarySchema).default([]),
  executiveInterpretation: z.array(z.string()).default([]),
  scopeNotes: z.array(z.string()).default([])
});

// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _ExecutiveReadinessVerdictZod, ExecutiveReadinessVerdict>);
export const ExecutiveReadinessVerdictSchema = _ExecutiveReadinessVerdictZod.transform((data) => new ExecutiveReadinessVerdict(data));


const _KeyRiskZod = z.object({
  id: z.string().default(""),
  label: z.string().default(""),
  harmCategory: z.string().default(""),
  riskRating: z.enum(RiskRanksWithNA).default("n/a")
})
// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _KeyRiskZod, KeyRisk>);
export const KeyRiskSchema = _KeyRiskZod.transform((data) => new KeyRisk(data));





const _DeploymentRequirementZod = z.object({
    label: z.string().default(""),
    requirement: z.string().default("")
})
// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _DeploymentRequirementZod, DeploymentRequirement>);
export const DeploymentRequirementSchema = _DeploymentRequirementZod.transform((data) => new DeploymentRequirement(data));

const _LeadershipSummaryZod = z.object({
    riskExposureSummary: z.string().default(""),
    keyRisks: z.array(KeyRiskSchema).default([]),
    deploymentRequirements: z.array(DeploymentRequirementSchema).default([])
})
// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _LeadershipSummaryZod, LeadershipSummary>);
export const LeadershipSummarySchema = _LeadershipSummaryZod.transform((data) => new LeadershipSummary(data));


const _RiskDriverZod = z.object({
    label: z.string().default(""),
    description: z.string().default("")
})

// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _RiskDriverZod, RiskDriver>);
export const RiskDriverSchema = _RiskDriverZod.transform((data) => new RiskDriver(data));



const _UseCaseOverviewZod = z.object({
  useCaseSummary: z.string().default(""),
  intendedPurposes: z.array(z.string()).default([]),
  intendedUsers: z.array(z.string()).default([]),
  deploymentContexts: z.array(z.string()).default([]),
  dataContexts: z.array(z.string()).default([]),
  keyStakeholders: z.array(z.string()).default([]),
  riskDrivers: z.array(RiskDriverSchema).default([])
})
// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _UseCaseOverviewZod, UseCaseOverview>);

export const UseCaseOverviewSchema = _UseCaseOverviewZod.transform((data) => new UseCaseOverview(data));


const _DeploymentCharacteristicZod = z.object({
  label: z.string().default(""),
  description: z.string().default("")
})

// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _DeploymentCharacteristicZod, DeploymentCharacteristic>);
export const DeploymentCharacteristicSchema = _DeploymentCharacteristicZod.transform((data) => new DeploymentCharacteristic(data));



const _OrganisationalImpactZod = z.object({
  label: z.string().default(""),
  description: z.string().default("")
})

// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _OrganisationalImpactZod, OrganisationalImpact>);
export const OrganisationalImpactSchema = _OrganisationalImpactZod.transform((data) => new OrganisationalImpact(data));


const _DeploymentProfileZod = z.object({
    deploymentCharacteristics: z.array(DeploymentCharacteristicSchema).default([]),
    organisationalImpacts: z.array(OrganisationalImpactSchema).default([]),
    riskConcentration: z.string().default(""),
    aiSystemImpact: AISystemImpactSchema.default(new AISystemImpact()),
})
// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _DeploymentProfileZod, DeploymentProfile>);
export const DeploymentProfileSchema = _DeploymentProfileZod.transform((data) => new DeploymentProfile(data));



const _DeploymentRequirementsSectionZod = z.object({
    deploymentRequirementSummary: z.string().default(""),
    mitigationRequirements: z.array(MitigationRequirementSchema).default([]),
    disclaimerNotes: z.array(z.string()).default([])
})
// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _DeploymentRequirementsSectionZod, DeploymentRequirementsSection>);
export const DeploymentRequirementsSectionSchema = _DeploymentRequirementsSectionZod.transform((data) => new DeploymentRequirementsSection(data));





const _RiskProfilePillarZod = z.object({
  label: z.string().default(""),
  riskRank: z.enum(RiskRanksWithNA).default("n/a"),
  characteristicsHistogram: z.record(z.enum(RiskRanksWithNA), z.number()).default({
    "n/a": 0,
    "negligible": 0,
    "low": 0,
    "moderate": 0,
    "high": 0,
    "critical": 0
  }),
  histogramComment: z.string().default(""),
  executiveComment: z.string().default(""),
  summary: z.string().default("")
}); 
// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _RiskProfilePillarZod, RiskProfilePillar>);
export const RiskProfilePillarSchema = _RiskProfilePillarZod.transform((data) => new RiskProfilePillar(data));



const _RiskProfileSummaryZod = z.object({
  overallRiskRank: z.enum(RiskRanksWithNA).default("n/a"),
  pillars: z.array(RiskProfilePillarSchema).default([])
});
// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _RiskProfileSummaryZod, RiskProfileSummary>);
export const RiskProfileSummarySchema = _RiskProfileSummaryZod.transform((data) => new RiskProfileSummary(data));



const _RiskPlusReportZod = z.object({
  id: z.string().default(""),
  useCaseChat: z.string().default(""),
  titlePage: RiskPlusTitlePageSchema.default(new RiskPlusTitlePage()),
  executiveReadinessVerdict: ExecutiveReadinessVerdictSchema.default(new ExecutiveReadinessVerdict()),
  leadershipSummary: LeadershipSummarySchema.default(new LeadershipSummary()),
  useCaseOverview: UseCaseOverviewSchema.default(new UseCaseOverview()),
  riskRegister: z.array(RiskRegistryItemSchema).default([]),
  deploymentProfile: DeploymentProfileSchema.default(new DeploymentProfile()),
  deploymentRequirements: DeploymentRequirementsSectionSchema.default(new DeploymentRequirementsSection()),
  riskProfileSummary: RiskProfileSummarySchema.default(new RiskProfileSummary())
}); 
// Assert that schema matches the class definition
void (true as AssertSchemaMatchesClass<typeof _RiskPlusReportZod, RiskPlusReport>);
export const RiskPlusReportSchema = _RiskPlusReportZod.transform((data) => new RiskPlusReport(data));


